import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCreateListingDialog } from "@/contexts/create-listing-dialog-provider";
import { useListings } from "@/contexts/listings-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useUser } from "@/contexts/user-provider";
import { useUsers } from "@/contexts/users-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import { EmployeeDTO, ListingDTO } from "@shaarp/common";
import { ChevronLeftIcon, ChevronRightIcon, FolderInputIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { toast, useToastError } from "../ui/use-toast";

type DataTableToolbarEmployeesListingActionProps = {
  employees: EmployeeDTO[];
  fetchDataLoading: boolean;
  searchQuery?: string;
};

export function DataTableToolbarEmployeesListingAction({
  employees,
  fetchDataLoading,
  searchQuery,
}: DataTableToolbarEmployeesListingActionProps) {
  const toastError = useToastError();
  const api = useApi();
  const {
    userListings,
    shaarpListings,
    collaboratorsListings,
    apiFetchListings,
    apiFetchCollaboratorsListings,
    trackListingPopulation,
  } = useListings();
  const { isAdmin, canViewCollaborators } = useTeams();
  const { teamUser } = useUser();
  const { users, apiFetchUsers } = useUsers();
  const { t } = useTranslation();
  const { openCreateListingDialog } = useCreateListingDialog();

  const [open, setOpen] = useState<boolean>(false);
  const [viewUserId, setViewUserId] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      await apiFetchListings();
      if (canViewCollaborators) {
        await apiFetchCollaboratorsListings();
        await apiFetchUsers();
      }
    };
    init();
  }, [canViewCollaborators]);

  useEffect(() => {
    if (!open) {
      setViewUserId(null);
    }
  }, [open]);

  const employeeUserListings = userListings.filter(
    (l) => l.listingType === "employees"
  );
  const employeeShaarpListings = isAdmin
    ? shaarpListings.filter((l) => l.listingType === "employees")
    : [];

  const teamMembers = useMemo(
    () => (canViewCollaborators ? users.filter((u) => u.id !== teamUser?.id) : []),
    [users, teamUser?.id, canViewCollaborators]
  );

  const viewedUser = teamMembers.find((u) => u.id === viewUserId);
  const viewedUserListings = viewUserId
    ? (collaboratorsListings.find((c) => c.user.id === viewUserId)?.listings ?? [])
        .filter((l) => l.listingType === "employees" && l.ownerId === viewUserId)
    : [];

  const hasRootContent =
    employeeUserListings.length > 0 ||
    employeeShaarpListings.length > 0 ||
    teamMembers.length > 0;

  const employeesIds = employees.map((e) => e.id);

  /** Local table + sidebar state, once the employees are attached server-side. */
  const onEmployeesAttached = async (listing: ListingDTO) => {
    employees.forEach((employee) => {
      employee.listingsIds?.push(listing.id);
    });
    await apiFetchListings({ force: true });
    toast({
      variant: "default",
      title: t(
        "components.dataTableCompanyEmployees.myLists.toolbar.addToList",
        {
          title: listing.title,
        }
      ),
      description: t(
        "components.dataTableCompanyEmployees.myLists.toolbar.employeesAdded",
        { count: employees.length }
      ),
    });
  };

  const selectListing = async (listing: ListingDTO) => {
    try {
      await trackListingPopulation(listing.id, async () => {
        await api.post(`/listing/${listing.id}/employees`, {
          employeesIds: employeesIds,
        });
        await onEmployeesAttached(listing);
      });
    } catch (error) {
      toastError();
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="hidden ml-auto h-8 lg:flex"
            disabled={fetchDataLoading || employees.length === 0}
          >
            <FolderInputIcon className="mr-2 w-4 h-4" />
            {t("components.dataTableCompanyEmployees.myLists.saveToList")}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0">
          <Command>
            {viewUserId && viewedUser ? (
              <>
                <CommandList>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => setViewUserId(null)}
                      className="text-sm text-muted-foreground"
                    >
                      <ChevronLeftIcon className="mr-2 w-4 h-4" />
                      {viewedUser.fullName}
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setOpen(false);
                        openCreateListingDialog({
                          defaultListingType: "employees",
                          defaultAssigneeId: viewedUser.id,
                          searchQuery,
                          employeesIds,
                          onCreated: onEmployeesAttached,
                        });
                      }}
                    >
                      <PlusCircledIcon className="mr-2 w-5 h-5" />
                      {t(
                        "components.dataTableCompanyEmployees.myLists.toolbar.newListFor",
                        { name: viewedUser.firstName || viewedUser.fullName }
                      )}
                    </CommandItem>
                  </CommandGroup>
                  <CommandInput
                    placeholder={t(
                      "components.dataTableCompanyEmployees.myLists.toolbar.search"
                    )}
                  />
                  <CommandEmpty>
                    {t(
                      "components.dataTableCompanyEmployees.myLists.toolbar.noListFound"
                    )}
                  </CommandEmpty>
                  {viewedUserListings.length > 0 ? (
                    <CommandGroup>
                      {viewedUserListings.map((listing) => (
                        <CommandItem
                          key={listing.id}
                          value={listing.title}
                          onSelect={() => {
                            selectListing(listing);
                            setOpen(false);
                          }}
                          className="text-sm"
                        >
                          {listing.title}
                          {listing.employeesCount === 0 ? (
                            <Badge variant="outline" className="ml-auto">
                              {t(
                                "components.dataTableCompanyEmployees.myLists.toolbar.empty"
                              )}
                            </Badge>
                          ) : (
                            <Badge variant="default" className="ml-auto">
                              {listing.employeesCount}
                            </Badge>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ) : (
                    <CommandGroup>
                      <CommandItem disabled={true}>
                        {t(
                          "components.dataTableCompanyEmployees.myLists.toolbar.noListForMember"
                        )}
                      </CommandItem>
                    </CommandGroup>
                  )}
                </CommandList>
              </>
            ) : (
              <>
                {hasRootContent ? (
                  <CommandList>
                    <CommandInput
                      placeholder={t(
                        "components.dataTableCompanyEmployees.myLists.toolbar.search"
                      )}
                    />
                    <CommandEmpty>
                      {t(
                        "components.dataTableCompanyEmployees.myLists.toolbar.noListFound"
                      )}
                    </CommandEmpty>
                    <CommandGroup
                      key="listings"
                      heading={t(
                        "components.dataTableCompanyEmployees.myLists.toolbar.myLists"
                      )}
                    >
                      {employeeUserListings.length === 0 &&
                      employeeShaarpListings.length === 0 ? (
                        <CommandItem disabled={true}>
                          {t(
                            "components.dataTableCompanyEmployees.myLists.toolbar.noListCreated"
                          )}
                        </CommandItem>
                      ) : (
                        [
                          ...employeeUserListings,
                          ...employeeShaarpListings,
                        ].map((listing) => (
                          <CommandItem
                            key={listing.id}
                            value={listing.title}
                            onSelect={() => {
                              selectListing(listing);
                              setOpen(false);
                            }}
                            className="text-sm"
                          >
                            {listing.title}
                            {listing.employeesCount === 0 ? (
                              <Badge variant="outline" className="ml-auto">
                                {t(
                                  "components.dataTableCompanyEmployees.myLists.toolbar.empty"
                                )}
                              </Badge>
                            ) : (
                              <Badge variant="default" className="ml-auto">
                                {listing.employeesCount}
                              </Badge>
                            )}
                          </CommandItem>
                        ))
                      )}
                    </CommandGroup>
                    {teamMembers.length > 0 && (
                      <CommandGroup
                        key="team"
                        heading={t(
                          "components.dataTableCompanyEmployees.myLists.toolbar.team"
                        )}
                      >
                        {teamMembers.map((member) => (
                          <CommandItem
                            key={member.id}
                            value={member.fullName}
                            onSelect={() => setViewUserId(member.id)}
                            className="flex gap-2 items-center text-sm"
                          >
                            <Avatar className="w-5 h-5">
                              <AvatarImage src={member.imageUrl} />
                              <AvatarFallback className="text-xs">
                                {member.fullName?.charAt(0) || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate">{member.fullName}</span>
                            <ChevronRightIcon className="ml-auto w-4 h-4 opacity-50" />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                ) : (
                  <CommandGroup
                    heading={t(
                      "components.dataTableCompanyEmployees.myLists.toolbar.myLists"
                    )}
                  >
                    <CommandItem disabled={true}>
                      {t(
                        "components.dataTableCompanyEmployees.myLists.toolbar.noListCreated"
                      )}
                    </CommandItem>
                  </CommandGroup>
                )}
                <CommandSeparator />
                <CommandList>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setOpen(false);
                        openCreateListingDialog({
                          defaultListingType: "employees",
                          searchQuery,
                          employeesIds,
                          onCreated: onEmployeesAttached,
                        });
                      }}
                    >
                      <PlusCircledIcon className="mr-2 w-5 h-5" />
                      {t(
                        "components.dataTableCompanyEmployees.myLists.toolbar.newList"
                      )}
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
}
